/**
 * encryptedSessionStorage — Web
 *
 * Wraps the browser's native sessionStorage with AES-GCM 256-bit encryption.
 * Values are encrypted (using the same master key as secureStorage / IndexedDB)
 * before being written, so DevTools → Application → Session Storage shows only
 * opaque base-64 ciphertext — never plaintext.
 *
 * Characteristics inherited from sessionStorage:
 *  ✔ Tab-scoped: data is cleared automatically when the tab / browser window closes.
 *  ✔ Same-origin isolation: other origins cannot read the store.
 *
 * Encryption is provided by re-using the encryptValue / decryptValue helpers
 * from secureStorage.web.ts, which use the persistent AES-GCM master key stored
 * in IndexedDB.
 */

// Import directly from the web implementation — this file is itself .web.ts so
// it is only ever bundled/compiled for web targets.
import { encryptValue, decryptValue } from './secureStorage.web';

const encryptedSessionStorage = {
  /**
   * Synchronous raw read from sessionStorage — used by Redux slice initialState.
   * Returns the stored value as-is (plaintext if legacy, ciphertext if encrypted).
   * Callers must handle the case where the value is opaque ciphertext (e.g. try/catch on JSON.parse).
   * For a fully decrypted value use the async getItem() below.
   */
  getItemSync(key: string): string | null {
    try {
      if (typeof sessionStorage !== 'undefined') {
        return sessionStorage.getItem(key);
      }
    } catch {
      // sessionStorage unavailable (SSR, sandboxed environment, etc.)
    }
    return null;
  },

  /**
   * Read and decrypt a value from sessionStorage.
   * Returns null if the key is absent or decryption fails.
   */
  async getItem(key: string): Promise<string | null> {
    try {
      const encrypted = sessionStorage.getItem(key);
      if (!encrypted) return null;
      return await decryptValue(encrypted);
    } catch (error) {
      console.error('[encryptedSessionStorage] getItem error:', error);
      return null;
    }
  },

  /**
   * Encrypt a value and write it to sessionStorage.
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      const encrypted = await encryptValue(value);
      sessionStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('[encryptedSessionStorage] setItem error:', error);
    }
  },

  /**
   * Remove an entry from sessionStorage (no decryption needed).
   */
  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  },

  /**
   * Clear all entries from sessionStorage.
   */
  clear(): void {
    sessionStorage.clear();
  },
};

export default encryptedSessionStorage;